import { isEmpty, reduce, uniq } from "lodash";

import treeUtils from "../../../../utils/tree";
import { definitionActionTypes } from "../../../definition";
import { narrativeSettingsActionTypes } from "../../../definition/narrativeSettings";
import { actionTypes as itemActionTypes } from "../../../items/actions";
import { narrativeActionTypes, narrativeSelectors } from "../../../narrative";
import rootActionTypes from "../../../rootActionTypes";
import { getAffectedActionIds } from "./getIdsFromActions";

const individualActionTypes = [
  //#!if readonly !== true
// narrativeActionTypes.ADD_TO_END_OF_NARRATIVE
  //#!endif
];

const globalActionTypes = [
  //#!if readonly !== true
// narrativeActionTypes.MOVE_STRICT_NARRATIVE_ILLEGAL_CHILDREN_TO_LEGAL_PARENT,
// narrativeActionTypes.MOVE_DOCUMENTS_TO_TOP_OF_NARRATIVE_PARENTS,
// narrativeActionTypes.MOVE_IN_NARRATIVE,
// narrativeActionTypes.REMOVE_FROM_NARRATIVE,
// itemActionTypes.DELETE_ITEMS,
// itemActionTypes.CHANGE_ITEMS_OF_TYPE_TO_TYPE,
// itemActionTypes.CHANGE_TYPE,
// itemActionTypes.SET_NARRATIVE_LABEL,
// narrativeSettingsActionTypes.ADD_LABEL,
// narrativeSettingsActionTypes.CHANGE_LABEL,
// narrativeSettingsActionTypes.CHANGE_NUMBERING,
// narrativeSettingsActionTypes.DELETE_LABEL,
  //#!endif
  rootActionTypes.LOAD_FILE,
  rootActionTypes.UPDATE_FILE,
  rootActionTypes.LOAD_SAMPLE,
  definitionActionTypes.REPLACE_DEFINITIONS
];

// Changes:
//   - narrativePosition
//   - narrativeLabel
//   - narrativeAutoLabel
//   - narrativeDisplayLabel
//   - narrativePathLabel

const applyNarrativeUpdates = (
  state,
  action,
  newNarrative,
  prevNarrative,
  newItemsById,
  narrativeFolderTypeId,
  narrativeSettings
) => {
  // For now, lets just take the easy route:
  // If the narrative is changed, check everything again, but only change those whose
  // values have actually changed.

  let { affectedIds, matchedIndividual } = getAffectedActionIds(
    state,
    action,
    globalActionTypes,
    individualActionTypes
  );

  if (matchedIndividual) {
    // Pull out all of the affected ids from this set of actions
    // Items are affected if they have changed, or if their children have changed
    const additionalIds = reduce(
      affectedIds,
      (result, aId) => {
        // Add any of its children from this or previous narrative
        // as well as any of its parents
        const newSubTree = treeUtils.getSubTreeWithId(newNarrative, aId);
        const newChildren = newSubTree ? treeUtils.getIdsInTree(newSubTree) : [];
        const prevSubTree = treeUtils.getSubTreeWithId(prevNarrative, aId);
        const prevChildren = prevSubTree ? treeUtils.getIdsInTree(prevSubTree) : [];
        const newParent = treeUtils.findParentAndIndexOfNode(newNarrative, aId);
        const prevParent = treeUtils.findParentAndIndexOfNode(prevNarrative, aId);
        result.push(...newChildren);
        result.push(...prevChildren);
        if (newParent && newParent.parent) {
          result.push(newParent.parent);
        }
        if (prevParent && prevParent.parent) {
          result.push(prevParent.parent);
        }
        return result;
      },
      []
    );
    affectedIds = uniq([...affectedIds, ...additionalIds]);
  }

  if (!affectedIds || affectedIds.length === 0) {
    return state;
  }

  const narrativeLabels = narrativeSettings.labels;
  const narrativeNumbering = narrativeSettings.numbering;

  const narrativePositionsById = narrativeSelectors.getAllNarrativePositionsById(newNarrative);
  const narrativeIdsWithChildren = narrativeSelectors.getNarrativeIdsWithChildrenAsSet(newNarrative);

  const narrativeAutoLabelsById = narrativeSelectors.getNarrativeAutoLabelsById(
    newNarrative,
    narrativeLabels,
    newItemsById,
    narrativeFolderTypeId,
    narrativeNumbering
  );

  const narrativeDisplayLabelsById = narrativeSelectors.getNarrativeDisplayLabelsById(
    newNarrative,
    narrativeLabels,
    newItemsById,
    narrativeFolderTypeId,
    narrativeNumbering
  );

  const narrativePathLabelsById = narrativeSelectors.getNarrativePathLabelsById(
    newNarrative,
    narrativeLabels,
    newItemsById,
    narrativeFolderTypeId,
    narrativeNumbering
  );

  return state.withMutations(s => {
    affectedIds.forEach(cId => {
      const itemRec = state.get(cId);

      const narrativePosition = narrativePositionsById.get(cId);
      const narrativeAutoLabel = narrativeAutoLabelsById.get(cId);
      const narrativeDisplayLabel = narrativeDisplayLabelsById.get(cId);
      const narrativePathLabel = narrativePathLabelsById.get(cId);
      const newHasNarrative = narrativePosition && narrativePosition.size > 0;
      const prevHasNarrative = itemRec.narrativePosition && itemRec.narrativePosition.size > 0;
      const updateNarrativePosition =
        (newHasNarrative && !prevHasNarrative) ||
        (prevHasNarrative && !newHasNarrative) ||
        (newHasNarrative && prevHasNarrative && narrativePosition.join(".") !== itemRec.narrativePosition.join("."));

      const newHasChildren = narrativeIdsWithChildren.has(cId);

      let changes = {};

      if (updateNarrativePosition) {
        changes.narrativePosition = narrativePosition;
      }
      if (narrativeAutoLabel !== itemRec.narrativeAutoLabel) {
        changes.narrativeAutoLabel = narrativeAutoLabel;
      }
      if (narrativeDisplayLabel !== itemRec.narrativeDisplayLabel) {
        changes.narrativeDisplayLabel = narrativeDisplayLabel;
      }
      if (narrativePathLabel !== itemRec.narrativePathLabel) {
        changes.narrativePathLabel = narrativePathLabel;
      }
      if (newHasChildren !== itemRec.narrativeHasChildren) {
        changes.narrativeHasChildren = newHasChildren;
      }

      if (!isEmpty(changes)) {
        s.update(cId, d => d.merge(changes));
      }
    });
  });
};

export default applyNarrativeUpdates;
